import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, CalendarCheck, MapPin, Clock, LogOut } from "lucide-react";
import TopNav from "../components/TopNav";
import Orbs from "../components/Orbs";
import { Section, Overline } from "../components/Layout";
import { api } from "../lib/api";
import {
  requestCode, signIn, myEventDetails, getSession, getSessionInfo, clearSession,
} from "../lib/register";

// Status → card badge (mirrors the Events list badges).
const STATUS_BADGE = {
  going:      { label: "Registered",   cls: "chip-green" },
  checked_in: { label: "Attended",     cls: "chip-green" },
  pending:    { label: "Under review", cls: "chip-cream-2" },
  waitlist:   { label: "Waitlisted",   cls: "chip-purple" },
  invited:    { label: "Invited",      cls: "chip-cream-2" },
};

function Badge({ status }) {
  const b = STATUS_BADGE[status];
  if (!b) return null;
  return <span className={`chip ${b.cls} text-[10px]`}>{b.label}</span>;
}

function EventRow({ item, onCancel, past }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(`Cancel your spot for "${item.title}"?`)) return;
    setBusy(true);
    try {
      await onCancel(item);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="glass-panel p-4 flex items-start gap-4" data-testid={`my-event-${item.event_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={item.url} className="font-heading text-lg hover:underline truncate">{item.title}</Link>
          <Badge status={item.status} />
        </div>
        <div className="mt-1.5 text-sm text-muted-foreground flex items-center gap-1.5">
          <Clock size={13} /> {item.when}
        </div>
        {item.location && (
          <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
            <MapPin size={13} /> <span className="truncate">{item.location}</span>
          </div>
        )}
      </div>
      {!past && item.cancelable && (
        <button onClick={cancel} disabled={busy}
          className="btn-ghost text-xs shrink-0" style={{ color: "hsl(var(--vd-coral))" }}
          data-testid={`my-event-cancel-${item.event_id}`}>
          {busy ? "Cancelling…" : "Cancel"}
        </button>
      )}
    </div>
  );
}

// The signed-out state: email → 6-digit code → 30-day session.
function SignInPanel({ onSignedIn }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const em = email.trim().toLowerCase();
    if (!em.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      await requestCode(em);
      toast.success("Code sent — check your inbox");
      setStep("code");
    } catch (e) {
      if (e.retryAfter) toast.error(`Too many requests — try again in ${e.retryAfter}s`);
      else toast.error("We couldn't send a code, try again shortly");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code.trim())) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await signIn(email.trim().toLowerCase(), code.trim());
      toast.success("Signed in");
      onSignedIn();
    } catch (e) {
      const s = e.response?.status;
      if (s === 400) toast.error("Wrong or expired code — request a new one");
      else if (s === 503) toast.error("We couldn't confirm right now — please try again");
      else toast.error("Sign-in failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="glass-panel p-6 max-w-md mx-auto" data-testid="signin-panel">
      <Overline>{step === "email" ? "Sign in" : "Enter your code"}</Overline>
      <div className="mt-2 font-heading text-xl">See your events</div>
      <div className="text-sm text-muted-foreground mt-1">
        No password — we email you a 6-digit code. You'll stay signed in on this
        device for 30 days.
      </div>

      {step === "email" ? (
        <div className="mt-5 space-y-3">
          <div>
            <div className="overline mb-1 flex items-center gap-1.5"><Mail size={11} /> Email</div>
            <input type="email" className="input-field" placeholder="you@company.com" value={email}
              onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="signin-email" autoFocus />
          </div>
          <button onClick={send} disabled={busy} className="btn-primary w-full" data-testid="signin-send-code">
            {busy ? "Sending…" : "Send code"} <ArrowRight size={14} />
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <div className="text-xs text-muted-foreground">Code sent to <span className="font-mono">{email}</span>. Expires in 15 minutes.</div>
          <input inputMode="numeric" maxLength={6} className="input-field text-center tracking-[0.5em] font-mono text-lg" placeholder="••••••"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verify()}
            data-testid="signin-code" autoFocus />
          <button onClick={verify} disabled={busy} className="btn-primary w-full" data-testid="signin-submit">
            {busy ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
          </button>
          <button onClick={send} disabled={busy} className="btn-ghost w-full text-xs" data-testid="signin-resend">Resend code</button>
        </div>
      )}
    </div>
  );
}

export default function MyEvents() {
  const nav = useNavigate();
  const [session, setSession] = useState(getSessionInfo());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const d = await myEventDetails();
    setData(d);
    setLoading(false);
    if (!d) setSession(null); // session expired mid-load
  };

  useEffect(() => {
    if (session) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSignedIn = () => { setSession(getSessionInfo()); load(); };

  const signOut = () => { clearSession(); setSession(null); setData(null); nav("/events"); };

  const cancelOne = async (item) => {
    const t = getSession();
    try {
      await api.post("/cancel", { session_token: t, target_id: item.registration_id });
      toast.success("Registration cancelled");
      await load();
    } catch (e) {
      toast.error("Couldn't cancel — please try again");
    }
  };

  return (
    <div className="relative min-h-screen">
      <Orbs />
      <div className="relative z-10">
        <TopNav />
        <Section className="pt-10 pb-24" innerClassName="max-w-2xl mx-auto">
          {!session ? (
            <SignInPanel onSignedIn={onSignedIn} />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <Overline>My events</Overline>
                  <div className="mt-1 font-heading text-2xl flex items-center gap-2">
                    <CalendarCheck size={22} style={{ color: "hsl(var(--vd-green))" }} /> Your registrations
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{session.email}</div>
                </div>
                <button onClick={signOut} className="btn-ghost text-xs" style={{ color: "hsl(var(--vd-coral))" }} data-testid="my-events-signout">
                  <LogOut size={13} /> Sign out
                </button>
              </div>

              {loading && !data ? (
                <div className="mt-6 skeleton h-24" />
              ) : (
                <>
                  <div className="mt-6 space-y-3" data-testid="my-events-upcoming">
                    {(data?.upcoming || []).length === 0 ? (
                      <div className="glass-panel p-6 text-center text-sm text-muted-foreground">
                        Nothing upcoming yet. <Link to="/events" className="underline">Browse events</Link>.
                      </div>
                    ) : (
                      data.upcoming.map((it) => (
                        <EventRow key={it.registration_id} item={it} onCancel={cancelOne} />
                      ))
                    )}
                  </div>

                  {(data?.past || []).length > 0 && (
                    <div className="mt-8">
                      <Overline>Past</Overline>
                      <div className="mt-3 space-y-3 opacity-80" data-testid="my-events-past">
                        {data.past.map((it) => (
                          <EventRow key={it.registration_id} item={it} onCancel={cancelOne} past />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Section>
      </div>
    </div>
  );
}
